import React, { useState, useEffect, useMemo } from 'react';
import Plot from 'react-plotly.js';
import { ControlPanel, ControlGroup, Loader } from '../components/Components';
import './gene.css';
import Tooltip from './Tooltip';

// Searchable Select Component
const SearchableSelect = React.memo(({ options = [], value, onChange, placeholder, label }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        return options.filter(option =>
            option.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, options]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.searchable-select')) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    const handleSelect = (option) => {
        onChange(option);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className="searchable-select">
<div className="searchable-select-input" onClick={() => setIsOpen(true)}>
                <input
                    type="text"
                    value={isOpen ? searchTerm : value || ''}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={placeholder}
                    onFocus={() => setIsOpen(true)}
                    className="search-input"
                />
                <svg className="dropdown-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </div>

            {isOpen && (
                <div className="searchable-select-dropdown">
                    <div className="dropdown-content">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={index}
                                    className={`dropdown-option ${option === value ? 'selected' : ''}`}
                                    onClick={() => handleSelect(option)}
                                >
                                    {option}
                                </div>
                            ))
                        ) : (
                            <div className="dropdown-no-results">No results found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
});

function ImmunePage({ 
    selections, 
    handleSelection, 
    options, 
    data, 
    isTabLoading,
    diseaseColors 
}) {
    const immuneCellTypes = selections.immuneCellRef === 'HPA' ? options.immuneCellTypesHPA : options.immuneCellTypesMonaco;
    const shouldShowLoading = isTabLoading;

    // Clean plot configuration
    const plotConfig = {
        displayModeBar: false,
        responsive: true
    };

    const plotLayout = {
        font: { family: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif', size: 11 },
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(248, 250, 252, 0.5)',
        margin: { l: 50, r: 20, t: 0, b: 170 }, // Increased bottom margin for rotated labels
        showlegend: false
    };

    // Create box plot data with disease colors
    const createBoxPlotData = (plotData, yTitle) => {
        if (!plotData || plotData.length === 0) return [];

        return Array.from(new Set(plotData.map(d => d.disease) || [])).map(disease => ({
            y: plotData.filter(d => d.disease === disease).map(d => d.value),
            x: plotData.filter(d => d.disease === disease).map(d => d.disease),
            type: 'box',
            name: disease,
            boxpoints: 'all',
            jitter: 0.3,
            pointpos: 0,
            fillcolor: 'rgba(0,0,0,0)',
            line: { color: 'rgba(0,0,0,0.3)', width: 1 },
            marker: {
                color: diseaseColors[disease] || '#cccccc',
                size: 4,
                opacity: 0.7
            },
            hovertemplate: `<b>${disease}</b><br>${yTitle}: %{y:.3f}<extra></extra>`
        }));
    };

    return (
        <div className="gene-page">
            {/* Sidebar */}
            <div className="gene-sidebar">
                <h1 className="gene-sidebar-title">Immune Signatures</h1>
                
                {/* Cell Type Proportion Controls */}
                <div className="gene-sidebar-section">
                    <p className="gene-sidebar-label">Cell Type Proportion</p>
                    <div className="gene-mode-controls">
                        <button
                            className={`gene-mode-btn ${selections.immuneCellRef === 'HPA' ? 'active' : ''}`}
                            onClick={() => handleSelection('immuneCellRef', 'HPA')}
                        >
                            HPA Reference
                        </button>
                        <button
                            className={`gene-mode-btn ${selections.immuneCellRef === 'Monaco' ? 'active' : ''}`}
                            onClick={() => handleSelection('immuneCellRef', 'Monaco')}
                        >
                            Monaco Reference
                        </button>
                    </div>
                </div>

                <div className="gene-sidebar-section">
                    <p className="gene-sidebar-label">Cell Type</p>
                    <SearchableSelect
                        options={immuneCellTypes || []}
                        value={selections.immuneCellType}
                        onChange={(value) => handleSelection('immuneCellType', value)}
                        placeholder="Search cell types..."
                    />
                </div>

                {/* TCR/BCR Repertoire Controls */}
                <div className="gene-sidebar-section">
                    <p className="gene-sidebar-label">Repertoire Analysis</p>
                    <div className="gene-mode-controls">
                        <button
                            className={`gene-mode-btn ${selections.immuneDiversityCell === 'TCR' ? 'active' : ''}`}
                            onClick={() => handleSelection('immuneDiversityCell', 'TCR')}
                        >
                            TCR
                        </button>
                        <button
                            className={`gene-mode-btn ${selections.immuneDiversityCell === 'BCR' ? 'active' : ''}`}
                            onClick={() => handleSelection('immuneDiversityCell', 'BCR')}
                        >
                            BCR
                        </button>
                    </div>
                </div>

                <div className="gene-sidebar-section">
                    <p className="gene-sidebar-label">Diversity Metric</p>
                    <SearchableSelect
                        options={["cdr3 coverage", "dominant clone percent", "chao1", "shannon", "clonality"]}
                        value={selections.immuneDiversityMetric}
                        onChange={(value) => handleSelection('immuneDiversityMetric', value)}
                        placeholder="Search metrics..."
                    />
                </div>
            </div>

            {/* Main Content */}
            <div className="gene-main-content">
                {/* Loading State */}
                {shouldShowLoading && (
                    <div className="gene-loading">
                        <div className="gene-loading-spinner"></div>
                        Loading immune analysis data...
                    </div>
                )}

                {/* Content */}
                {!shouldShowLoading && (
                    <div className="gene-content">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            
                            {/* Cell Type Proportion Plot */}
                            <div className="gene-glass-card">
                                <div className="gene-card-title">
                                    Cell Type Proportion
                                    <span className="gene-tag">{selections.immuneCellType}</span>
                                </div>
                                <p className="gene-card-subtitle">
                                    Estimated cell type proportions across diseases ({selections.immuneCellRef} reference)
                                </p>
                                
                                <div className="gene-plot-container" style={{ height: '400px', overflow: 'hidden' }}>
                                    <Plot 
                                        data={createBoxPlotData(data.immunePropData, 'Estimated Proportion')}
                                        layout={{
                                            ...plotLayout,
                                            height: 400,
                                            xaxis: { 
                                                title: { text: 'Disease Type', font: { size: 14, color: '#374151' } },
                                                tickangle: -45,
                                                tickfont: { size: 11, color: '#6b7280' },
                                                showgrid: false,
                                                showline: true,
                                                linecolor: 'rgba(0,0,0,0.2)',
                                                linewidth: 1
                                            },
                                            yaxis: { 
                                                title: { text: 'Estimated Proportion', font: { size: 14, color: '#374151' } },
                                                tickfont: { size: 12, color: '#6b7280' },
                                                gridcolor: 'rgba(0,0,0,0.1)',
                                                showgrid: true
                                            }
                                        }}
                                        config={plotConfig}
                                        useResizeHandler={true}
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </div>
                            </div>

                            {/* TCR/BCR Repertoire Diversity Plot */}
                            <div className="gene-glass-card">
                                <div className="gene-card-title">
                                    {selections.immuneDiversityCell} Repertoire Diversity
                                    <span className="gene-tag">{selections.immuneDiversityMetric}</span>
                                </div>
                                <p className="gene-card-subtitle">
                                    {selections.immuneDiversityCell} repertoire diversity analysis using {selections.immuneDiversityMetric} metric
                                </p>
                                
                                <div className="gene-plot-container" style={{ height: '400px', overflow: 'hidden' }}>
                                    <Plot 
                                        data={createBoxPlotData(data.immuneDiversityData, selections.immuneDiversityMetric)}
                                        layout={{
                                            ...plotLayout,
                                            height: 400,
                                            xaxis: { 
                                                title: { text: 'Disease Type', font: { size: 14, color: '#374151' } },
                                                tickangle: -45,
                                                tickfont: { size: 11, color: '#6b7280' },
                                                showgrid: false,
                                                showline: true,
                                                linecolor: 'rgba(0,0,0,0.2)',
                                                linewidth: 1
                                            },
                                            yaxis: { 
                                                title: { text: selections.immuneDiversityMetric, font: { size: 14, color: '#374151' } },
                                                tickfont: { size: 12, color: '#6b7280' },
                                                gridcolor: 'rgba(0,0,0,0.1)',
                                                showgrid: true
                                            }
                                        }}
                                        config={plotConfig}
                                        useResizeHandler={true}
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ImmunePage;
